import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import { PlusIcon } from "@heroicons/react/outline";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { Button } from "../../Components/Button";
import {
  requestListBusiness,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import dayjs from "dayjs";
import { ErrorList } from "../../Components/ErrorList";
import { EmptyList } from "../../Components/EmptyList";
import { classNames } from "../../Utils";
import {
  BUSINESS_TYPES_OPTIONS,
  sortBusinessesForSwitch,
} from "../../Constants/business";
import { useFreeBusinessCreateBlocked } from "../../Hooks";
import BusinessStatusBadge from "../../Components/v2/Business/BusinessStatusBadge";
import { ModalConfirm } from "../../Components/ModalConfirm";

export const BUSINESS_CONFIG = [
  {
    value: "/account",
    label: "Accounts",
  },
  {
    value: "/partner",
    label: "Partners",
  },
  {
    value: "/product",
    label: "Products",
  },
  {
    value: "/tax",
    label: "Taxes",
  },
];

export const Business = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuthState();
  const navigate = useNavigate();

  const [selectedConfig, setselectedConfig] = useState(null);
  const [freeLimitModalOpen, setFreeLimitModalOpen] = useState(false);

  const dispatch = useBusinessTransactionDispatch();
  const { businesses, loading, error } = useBusinessTransactionState();

  // Same ordering as the business switcher: main first, then by health, ties on
  // name — so a business sits in the same place wherever the user looks for it.
  const sortedBusinesses = useMemo(
    () => sortBusinessesForSwitch(businesses, user.mainBusinessID),
    [businesses, user.mainBusinessID]
  );

  const createBlocked = useFreeBusinessCreateBlocked(businesses);

  useEffect(() => {
    if (user.id) {
      requestListBusiness(dispatch, { owner: user.id });
    }
  }, [dispatch, user.id]);

  useEffect(() => {
    if (selectedConfig?.value) {
      navigate(selectedConfig?.value);
    }
    setselectedConfig(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConfig?.value]);

  const onCreate = () => {
    if (createBlocked) {
      setFreeLimitModalOpen(true);
      return;
    }
    navigate("/business/create");
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <Button
          className="w-full sm:w-auto"
          type="button"
          onClick={onCreate}
          icon={PlusIcon}
          title={t("Business")}
          data-testid="create-new-business"
        />
        <div>
          <Listbox value={selectedConfig} onChange={setselectedConfig}>
            {({ open }) => (
              <>
                <ListboxButton className="flex items-center justify-between w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm text-sm">
                  <span className="ml-3 mr-2">
                    {t(selectedConfig?.label || t("Settings"))}
                  </span>
                  <SelectorIcon className="w-5 h-5 text-gray-500" />
                </ListboxButton>
                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-48 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                    {BUSINESS_CONFIG.map((report) => (
                      <ListboxOption
                        key={report.value}
                        className={({ focus }) =>
                          classNames(
                            focus ? "text-white bg-blue-600" : "text-gray-900",
                            "cursor-default select-none relative py-2 pl-3 pr-9"
                          )
                        }
                        value={report}
                      >
                        {({ selected, focus }) => (
                          <>
                            <div className="flex items-center justify-between">
                              <span
                                className={classNames(
                                  selected ? "font-semibold" : "font-normal",
                                  "ml-3 mr-2 block truncate"
                                )}
                              >
                                {t(report.label)}
                              </span>
                              {selected ? (
                                <span
                                  className={classNames(
                                    focus ? "text-white" : "text-blue-600",
                                    "absolute inset-y-0 right-0 flex items-center pr-4"
                                  )}
                                >
                                  <CheckIcon className="h-5 w-5" />
                                </span>
                              ) : null}
                            </div>
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </>
            )}
          </Listbox>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              {/* Table Header */}
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-900"
                  >
                    {t("ID")}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-900"
                  >
                    {t("Business Name")}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-900"
                  >
                    {t("Type")}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-900"
                  >
                    {t("Category")}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-900"
                  >
                    {t("Status")}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-900"
                  >
                    {t("Date Created")}
                  </th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody className="divide-y divide-gray-200 bg-white">
                {loading ? (
                  // Loading skeleton
                  [...Array(4)].map((_, index) => (
                    <tr key={`loading-${index}`}>
                      {[...Array(5)].map((_, cellIndex) => (
                        <td
                          key={`loading-cell-${cellIndex}`}
                          className="px-4 py-3"
                        >
                          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : sortedBusinesses.length > 0 ? (
                  // Business data
                  sortedBusinesses.map((business) => {
                    const shortId = business.id.substr(business.id.length - 5);

                    return (
                      <tr key={business.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <a
                            href={`/business/${business.id}`}
                            className="hover:text-blue-600"
                          >
                            {shortId}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            {business.name}
                            {business.id === user.mainBusinessID && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {t("Main")}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {(() => {
                            let typeClass = "bg-gray-100 text-gray-800";
                            if (business.type === "PERSONAL")
                              typeClass = "bg-green-100 text-green-800";
                            if (business.type === "BUSINESS")
                              typeClass = "bg-blue-100 text-blue-800";

                            return (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeClass}`}
                              >
                                {t(
                                  BUSINESS_TYPES_OPTIONS.find(
                                    (type) => type.value === business.type
                                  )?.label || business.type
                                )}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {t(business.category)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {business.status && business.status !== "ACTIVE" ? (
                            <BusinessStatusBadge business={business} />
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {t("Active")}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {dayjs(business.createdAt)
                            .locale(i18n.language)
                            .format("DD MMM YYYY")}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  // Empty or error state
                  <tr>
                    <td colSpan={6} className="px-4 py-3 text-center">
                      {error ? (
                        <ErrorList
                          message={error?.message || "Error fetching business."}
                          errors={error?.errors}
                        />
                      ) : (
                        <EmptyList title="No Business" />
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ModalConfirm
        open={freeLimitModalOpen}
        setOpen={setFreeLimitModalOpen}
        title={t("business.messages.freeLimitBlockTitle")}
        description={t("business.messages.freeLimitBlockDesc")}
        type="WARNING"
        confirmTitle={t("business.actions.subscribe")}
        onConfirm={() => {
          setFreeLimitModalOpen(false);
          navigate("/subscription");
        }}
      />
    </div>
  );
};
